import { Button, Stack, Tooltip } from "@altimateai/lego";
import { Input } from "@altimateai/lego";
import { SearchIcon } from "@assets/icons";
import { IconRefresh } from "@tabler/icons-react";
import ProjectTree from "../ProjectTree";
import { ProjectNode, TableauProject } from "../types";

interface ProjectTreeSectionProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  projectTree: ProjectNode[];
  selectedProject: TableauProject | null;
  onSelectProject: (project: TableauProject | null) => void;
  disabledProjectIds: Set<string>;
  /** Tooltip shown on disabled tree items. Threaded through to ProjectTree. */
  disabledTooltip?: string;
  error: unknown;
  /** Re-fetch the project list. When provided, a refresh icon button renders
   * next to the search input. Useful when the customer just created the
   * altimate_workbooks subproject in Tableau and wants to pick it up here. */
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const ProjectTreeSection = ({
  searchQuery,
  onSearchChange,
  projectTree,
  selectedProject,
  onSelectProject,
  disabledProjectIds,
  disabledTooltip,
  error,
  onRefresh,
  isRefreshing = false,
}: ProjectTreeSectionProps) => {
  return (
    <>
      <Stack direction="row" align="center" className="al-mb-4 al-gap-2">
        <div className="al-relative al-flex-1">
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="al-pl-10"
          />
          <SearchIcon className="al-absolute al-left-3 al-top-1/2 al--translate-y-1/2 al-w-4 al-h-4 al-text-gray-400" />
        </div>
        {onRefresh && (
          <Tooltip title="Refresh project list">
            <Button
              variant="outline"
              size="icon"
              title="Refresh project list"
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label="Refresh project list"
            >
              <IconRefresh size={16} className={isRefreshing ? "al-animate-spin" : undefined} />
            </Button>
          </Tooltip>
        )}
      </Stack>

      <div className="al-min-h-[400px] al-max-h-[500px] al-overflow-y-auto al-border al-rounded-md al-p-4 al-bg-gray-50">
        {error ? (
          <Stack direction="column" align="center" justify="center" className="al-py-8">
            <div className="al-text-red-600 al-text-center">
              <p className="al-font-semibold">Failed to load projects</p>
              <p className="al-text-sm al-mt-2">
                {(error as Error)?.message || "Please check your connection credentials"}
              </p>
            </div>
          </Stack>
        ) : projectTree.length === 0 ? (
          <Stack direction="column" align="center" justify="center" className="al-py-8">
            <div className="al-text-gray-500">
              {searchQuery ? "No projects found matching your search" : "No projects available"}
            </div>
          </Stack>
        ) : (
          <ProjectTree
            projects={projectTree}
            selectedProject={selectedProject}
            onSelectProject={onSelectProject}
            disabledProjectIds={disabledProjectIds}
            disabledTooltip={disabledTooltip}
          />
        )}
      </div>
    </>
  );
};
